import React, { useState } from 'react';
import { Text, Heading, Input, Button } from '../';
import axios from 'axios';
import { API_DOMAIN } from 'config';
import OfferPaymentConfirmModal from 'components/OfferPaymentConfirmModal';

const AuctionBidForm = ({ carId, auctionStatus, userId, onBidSuccess }) => {
  const [bidAmount, setBidAmount] = useState('');
  const [pendingBidAmount, setPendingBidAmount] = useState(null);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!auctionStatus?.is_auction) return null;
  if (!auctionStatus.has_started) return null;
  if (auctionStatus.has_ended) return null;

  const minimumBid = auctionStatus.minimum_bid || 0;
  const recommendedPrice = auctionStatus.recommended_price || 0;

  const buildUpdatedAuctionStatus = (bidResponse) => {
    const updatedEndTime =
      bidResponse.auction_active_until ||
      bidResponse.auction_end_time ||
      bidResponse.auction_end_at;

    return {
      ...auctionStatus,
      ...bidResponse,
      is_auction: true,
      status: bidResponse.status || 'active',
      has_started: true,
      has_ended: false,
      can_bid: bidResponse.can_bid ?? true,
      highest_bid: bidResponse.highest_bid ?? bidResponse.bid_amount ?? auctionStatus.highest_bid,
      minimum_bid: bidResponse.minimum_bid ?? bidResponse.new_minimum_bid ?? bidResponse.bid_amount ?? auctionStatus.minimum_bid,
      auction_active_until: updatedEndTime,
      auction_end_time: updatedEndTime,
      auction_end_at: updatedEndTime,
      auction_active_until_iso: bidResponse.auction_active_until_iso || auctionStatus.auction_active_until_iso,
      auction_end_time_iso: bidResponse.auction_end_time_iso || auctionStatus.auction_end_time_iso,
      server_now: bidResponse.server_now || bidResponse.server_time || auctionStatus.server_now,
      server_time: bidResponse.server_time || bidResponse.server_now || auctionStatus.server_time,
      server_now_iso: bidResponse.server_now_iso || auctionStatus.server_now_iso,
      remaining_seconds: Number.isFinite(Number(bidResponse.remaining_seconds))
        ? Number(bidResponse.remaining_seconds)
        : 300
    };
  };

  const submitBid = async (amount, { openPaymentOnMissingHold = false } = {}) => {
    setError('');
    setSuccess('');

    setLoading(true);

    try {
      const response = await axios.post(`${API_DOMAIN}/car/submitAuctionBid`, {
        car_id: carId,
        user_id: userId,
        bid_amount: amount
      });

      if (response.data.success) {
        setSuccess(response.data.message || 'ההצעה נקלטה בהצלחה');
        setBidAmount('');
        if (onBidSuccess) {
          await onBidSuccess(buildUpdatedAuctionStatus(response.data));
        }
      } else {
        setError(response.data.error || 'שגיאה בשליחת ההצעה');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'שגיאה בשליחת ההצעה';
      setError(errorMsg);
      if (openPaymentOnMissingHold && err.response?.data?.code === 'NEEDS_AUCTION_HOLD') {
        setPendingBidAmount(amount);
        setShowPaymentConfirm(true);
        return;
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!userId) {
      setError('יש להתחבר כסוחר כדי להגיש הצעה');
      return;
    }

    const amount = parseFloat(bidAmount);
    if (!amount || amount <= 0) {
      setError('נא להזין סכום תקין');
      return;
    }

    if (auctionStatus.highest_bid && amount <= Number(auctionStatus.highest_bid)) {
      setError(`ההצעה חייבת להיות גבוהה מ- ₪${Number(auctionStatus.highest_bid).toLocaleString()}`);
      return;
    }

    if (amount < recommendedPrice) {
      setError(`ההצעה לא יכולה להיות נמוכה מהמחיר המומלץ: ₪${recommendedPrice.toLocaleString()}`);
      return;
    }

    try {
      await submitBid(amount, { openPaymentOnMissingHold: true });
    } catch {
      // submitBid already surfaced the backend error in the form.
    }
  };

  const formatPrice = (price) => {
    return price ? `₪${price.toLocaleString()}` : '₪0';
  };

  return (
    <div className="flex flex-col gap-4 rounded-[20px] bg-white p-6 shadow-md">
      <OfferPaymentConfirmModal
        open={showPaymentConfirm}
        userId={userId}
        car={{ id: carId, is_auction: 1 }}
        offerAmount={pendingBidAmount || 0}
        recommendedPrice={recommendedPrice}
        wantsReservation
        onClose={() => {
          setShowPaymentConfirm(false);
          setPendingBidAmount(null);
        }}
        onSubmitConfirmed={async () => {
          await submitBid(pendingBidAmount);
          setShowPaymentConfirm(false);
          setPendingBidAmount(null);
        }}
      />
      <div className="flex flex-col gap-2">
        <Heading size="heading4xl" as="h4" className="text-[22px] font-bold !text-black-900">
          הגשת הצעה למכרז
        </Heading>
        <Text size="textmd" as="p" className="!text-gray-600">
          הצעות החל מ- <span className="font-bold text-red-a700_03">{formatPrice(minimumBid)}</span>
        </Text>
        {auctionStatus.highest_bid && (
          <Text size="textmd" as="p" className="!text-gray-600">
            הצעה גבוהה ביותר: <span className="font-bold">{formatPrice(auctionStatus.highest_bid)}</span>
          </Text>
        )}
        {!auctionStatus.highest_bid && (
          <Text size="textmd" as="p" className="!text-gray-600">
            מחיר מומלץ: <span className="font-bold">{formatPrice(recommendedPrice)}</span>
          </Text>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-[16px] font-medium !text-black-900">
            סכום ההצעה (₪)
          </label>
          <Input
            type="number"
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            placeholder={`מינימום ${minimumBid.toLocaleString()}`}
            disabled={loading}
            className="rounded-[12px] border border-gray-400 p-3 text-[18px]"
            min={minimumBid}
            step="100"
          />
        </div>

        {error && (
          <div className="rounded-[12px] bg-red-50 p-3">
            <Text size="textmd" className="!text-red-700">
              {error}
            </Text>
          </div>
        )}

        {success && (
          <div className="rounded-[12px] bg-green-50 p-3">
            <Text size="textmd" className="!text-green-700">
              {success}
            </Text>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            disabled={loading}
            size="lg"
            className="w-full rounded-[16px] bg-red-a700_03 px-6 py-4 font-bold !text-white hover:bg-red-800 disabled:opacity-50"
          >
            {loading ? 'שולח...' : 'שלח הצעה'}
          </Button>
          <Text size="textxs" as="p" className="text-center !text-gray-500">
            השתתפות במכרז דורשת תפיסת מסגרת של 2,000 ₪
          </Text>
        </div>
      </form>

      <div className="rounded-[12px] bg-amber-50 p-3">
        <Text size="textmd" as="p" className="!text-amber-800">
          ⚠️ כל הצעה חדשה מאריכה את המכרז ב-5 דקות נוספות
        </Text>
      </div>
    </div>
  );
};

export default AuctionBidForm;
